import { useState, useEffect, useRef } from 'react';
import { TrendingUp, TrendingDown, Clock, DollarSign, Zap, Wallet } from 'lucide-react';
import { toast } from 'sonner';
import { binaryAPI } from '../../services/api';
import TradingChart from './TradingChart';
import { useUnifiedBalance } from '../../hooks/useUnifiedBalance';
import CryptoIcon from '../CryptoIcon';
import { formatPair, getCommodity } from '../../utils/commodities';
import { fmtPrice, fmtFixed, fmtNumber } from '../../utils/format';

const BinaryTradingView = ({ crypto }) => {
  const { totalBalance, availableBalance, holdings, prices, refresh: refreshBalance } = useUnifiedBalance();
  const [prediction, setPrediction] = useState('up');
  const [amount, setAmount] = useState('');
  const [duration, setDuration] = useState('60');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeTrades, setActiveTrades] = useState([]);
  const [config, setConfig] = useState([]);
  const [currentTime, setCurrentTime] = useState(new Date()); // For countdown re-render

  // Update countdown every second - always run when there are active trades
  useEffect(() => {
    if (activeTrades.length === 0) return;
    
    // Update current time every second to trigger re-render
    const interval = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    
    return () => clearInterval(interval);
  }, [activeTrades.length]);
  
  // Track which trades have been settled to prevent duplicates
  const settledTradesRef = useRef(new Set());
  
  // Check for ended trades and auto-settle them
  useEffect(() => {
    if (activeTrades.length === 0) return;
    
    const checkEndedInterval = setInterval(async () => {
      const now = new Date();
      const endedTrades = activeTrades.filter(t => {
        const hasEnded = new Date(t.end_time) <= now;
        const notYetSettled = !settledTradesRef.current.has(t.id);
        return hasEnded && notYetSettled && t.status === 'active';
      });
      
      if (endedTrades.length > 0) {
        // Settle each ended trade
        for (const trade of endedTrades) {
          // Mark as settling to prevent duplicate attempts
          settledTradesRef.current.add(trade.id);
          
          try {
            console.log(`Auto-settling trade ${trade.id}...`);
            const result = await binaryAPI.settleTrade(trade.id);
            console.log('Settlement result:', result);
            
            // The app-wide TradeWatcher shows the result report
            window.dispatchEvent(new CustomEvent('phantomtrades:trade-settled', { detail: { trade_id: trade.id, trade_type: 'binary', ...result } }));

            // Immediately refresh balance after each settlement
            refreshBalance();
            
          } catch (error) {
            // Trade might already be settled - remove from tracking
            console.log('Trade settlement error (may already be settled):', error.message);
          }
        }
        
        // Refresh active trades list
        try {
          const trades = await binaryAPI.getActiveTrades();
          setActiveTrades(trades);
        } catch (error) {
          console.error('Error refreshing trades:', error);
        }
      }
    }, 1500); // Check every 1.5 seconds for faster settlements
    
    return () => clearInterval(checkEndedInterval);
  }, [activeTrades, refreshBalance]);
  
  // Get selected crypto holding balance
  const getHoldingBalance = (symbol) => {
    if (!holdings || !symbol) return 0;
    const holding = holdings.find(h => h.crypto_symbol?.toUpperCase() === symbol.toUpperCase());
    return holding?.available_balance || holding?.amount || 0;
  };
  
  // Get USD value of a crypto holding
  const getHoldingUsdValue = (symbol, amount) => {
    if (!amount) return 0;
    if (symbol === 'USDT') return amount;
    const price = crypto?.current_price || prices?.[symbol] || 0;
    return amount * price;
  };

  // Binary trading durations and amounts based on backend config
  const durations = [
    { value: 30, label: '30s', minAmount: 500, maxAmount: 1000, profitRate: 0.10 },
    { value: 60, label: '1m', minAmount: 1001, maxAmount: 5000, profitRate: 0.12 },
    { value: 120, label: '2m', minAmount: 5001, maxAmount: 50000, profitRate: 0.14 },
    { value: 300, label: '5m', minAmount: 50001, maxAmount: 150000, profitRate: 0.16 },
    { value: 480, label: '8m', minAmount: 150001, maxAmount: 300000, profitRate: 0.18 },
    { value: 720, label: '12m', minAmount: 300001, maxAmount: 500000, profitRate: 0.20 },
    { value: 900, label: '15m', minAmount: 500001, maxAmount: 1000000, profitRate: 0.25 },
  ];

  const selectedDuration = durations.find(d => d.value === parseInt(duration));

  // Same real-time feed the backend uses for entry/exit prices
  const [livePrice, setLivePrice] = useState(null);
  useEffect(() => {
    if (!crypto?.id) return undefined;
    let alive = true;
    setLivePrice(null);
    const load = () => binaryAPI.getLivePrice(crypto.id, crypto.symbol)
      .then((d) => { if (alive) setLivePrice(d); })
      .catch(() => {});
    load();
    const iv = setInterval(load, 2000);
    return () => { alive = false; clearInterval(iv); };
  }, [crypto?.id, crypto?.symbol]);

  // Calculate potential return
  const calculateReturn = () => {
    if (!amount || !selectedDuration) return { return: '0.00', profit: '0.00', profitRate: '0%' };
    
    const amt = parseFloat(amount);
    const profit = amt * selectedDuration.profitRate;
    const totalReturn = amt + profit;
    
    return {
      return: fmtFixed(totalReturn, 2),
      profit: fmtFixed(profit, 2),
      profitRate: `${(selectedDuration.profitRate * 100).toFixed(0)}%`,
    };
  };

  const returns = calculateReturn();

  // Track previous trade count for settlement detection
  const [prevTradeCount, setPrevTradeCount] = useState(0);
  
  // Load active trades
  const loadActiveTrades = async () => {
    try {
      const trades = await binaryAPI.getActiveTrades();
      
      // Filter out expired trades (shouldn't happen but safety check)
      const now = new Date();
      const validTrades = trades.filter(trade => {
        if (!trade.start_time || !trade.duration_seconds) return true;
        const startTime = new Date(trade.start_time);
        const elapsedSeconds = (now.getTime() - startTime.getTime()) / 1000;
        // Keep trade if it's still within duration (with 5 second buffer for settlement)
        return elapsedSeconds < (trade.duration_seconds + 5);
      });
      
      // Check if any trade was settled (count decreased)
      if (prevTradeCount > 0 && validTrades.length < prevTradeCount) {
        // A trade was settled - refresh balance
        refreshBalance();
      }
      
      setPrevTradeCount(validTrades.length);
      setActiveTrades(validTrades);
    } catch (error) {
      console.error('Error loading active trades:', error);
    }
  };

  // Load active trades on mount
  useEffect(() => {
    loadActiveTrades();
  }, []);

  const handleTrade = async () => {
    if (!amount || parseFloat(amount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    const amt = parseFloat(amount);
    const dur = parseInt(duration);

    if (!selectedDuration) {
      toast.error('Please select a duration');
      return;
    }

    // Validate amount range
    if (amt < selectedDuration.minAmount || amt > selectedDuration.maxAmount) {
      toast.error(`For ${selectedDuration.label} duration, amount must be between $${selectedDuration.minAmount.toLocaleString()} - $${selectedDuration.maxAmount.toLocaleString()}`);
      return;
    }

    // Check USDT balance (all trades debit/credit USDT regardless of crypto predicted)
    const usdtBalance = getHoldingBalance('USDT') || 0;
    if (amt > usdtBalance) {
      toast.error(`Insufficient USDT balance. Available: $${fmtFixed(usdtBalance, 2)}`);
      return;
    }

    setIsSubmitting(true);

    try {
      const result = await binaryAPI.placeTrade({
        crypto_id: crypto.id,
        crypto_symbol: crypto.symbol,
        prediction: prediction,
        amount: amt,
        trade_currency: 'USDT',  // All trades always use USDT
        duration_seconds: dur,
      });
      window.dispatchEvent(new Event('phantomtrades:trade-placed'));
      
      toast.success(
        `🎯 Trade Placed! ${prediction.toUpperCase()} on ${formatPair(crypto.symbol)} - Amount: $${fmtFixed(amt, 2)}`,
        { duration: 4000 }
      );

      // Reload active trades and refresh balance
      await loadActiveTrades();
      refreshBalance();

      // Reset form
      setAmount('');
      
      // Scroll to active trades section after a brief delay
      setTimeout(() => {
        const activeSection = document.getElementById('active-trades-section');
        if (activeSection) {
          activeSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 300);

    } catch (error) {
      console.error('Binary trade error:', error);
      const { handleKycGateError } = await import('../../utils/kycGateToast.js');
      if (!handleKycGateError(error, (path) => (window.location.href = path))) {
        const errorMsg = error.response?.data?.detail || 'Failed to place binary trade';
        toast.error(typeof errorMsg === 'string' ? errorMsg : 'Failed to place binary trade');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Calculate countdown for active trades - with robust validation
  const getCountdown = (endTime) => {
    if (!endTime) return '0s';
    
    try {
      const end = new Date(endTime);
      if (isNaN(end.getTime())) return '0s';
      
      const now = new Date();
      const diff = Math.max(0, Math.floor((end - now) / 1000));
      
      if (diff >= 60) {
        const minutes = Math.floor(diff / 60);
        const seconds = diff % 60;
        return `${minutes}m ${seconds}s`;
      }
      return `${diff}s`;
    } catch {
      return '0s';
    }
  };

  return (
    <div className="space-y-4">
      {/* Trading Chart */}
      <TradingChart symbol={crypto.symbol} />

      {/* Trading Form */}
      <div className="glow-card p-4 sm:p-6 rounded-2xl">
        <h3 data-testid="binary-trading-title" className="text-lg sm:text-xl font-bold text-white mb-4 sm:mb-6 flex items-center">
          {getCommodity(crypto.symbol)
            ? <CryptoIcon symbol={crypto.symbol} size={28} className="mr-2 shrink-0" />
            : <Zap className="h-5 w-5 sm:h-6 sm:w-6 mr-2 text-yellow-400" />}
          Binary Options Trading - {formatPair(crypto.symbol)}
        </h3>

      {/* Balance & Price Display */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mb-4 sm:mb-6">
        <div className="bg-black/30 rounded-lg p-3 sm:p-4">
          <div className="text-xs sm:text-sm text-gray-400 mb-1 flex items-center gap-2">
            Current Price
            {livePrice?.source && livePrice.source !== 'coingecko' && (
              <span className="flex items-center gap-1 text-[10px] font-bold text-emerald-400" data-testid="binary-live-badge">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" /> LIVE
              </span>
            )}
          </div>
          <div className="text-xl sm:text-2xl md:text-3xl font-bold text-yellow-400" data-testid="binary-current-price">
            ${fmtPrice(livePrice?.price || crypto.current_price)}
          </div>
        </div>
        <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-3 sm:p-4">
          <div className="text-xs sm:text-sm text-gray-400 mb-1 flex items-center">
            <Wallet className="h-3 w-3 sm:h-4 sm:w-4 mr-1" /> Trade Using
          </div>
          <div
            data-testid="trade-currency-label"
            className="text-green-400 text-lg sm:text-xl font-bold mb-1"
          >
            USDT
          </div>
          <div className="text-xs sm:text-sm text-gray-400">
            <span className="block sm:inline">
              Balance: {fmtFixed((getHoldingBalance('USDT') || 0), 2)} USDT
            </span>
          </div>
        </div>
      </div>

      {/* Active Trades Display with Circular Countdown */}
      {activeTrades.length > 0 && (
        <div id="active-trades-section" className="mb-6">
          <h4 className="text-lg font-bold text-green-400 mb-4 flex items-center">
            <span className="w-3 h-3 bg-green-500 rounded-full mr-2 animate-pulse"></span>
            Active Trades ({activeTrades.length})
          </h4>
          {activeTrades.map((trade) => {
            // Get duration directly from trade - this is the source of truth
            const tradeDuration = trade.duration_seconds || 30;
            
            // Calculate remaining time from end_time (more reliable than start_time calculation)
            let endTime;
            try {
              endTime = trade.end_time ? new Date(trade.end_time) : null;
              // Validate the date
              if (endTime && isNaN(endTime.getTime())) {
                endTime = null;
              }
            } catch {
              endTime = null;
            }
            
            // Fallback: calculate end_time from start_time + duration if end_time is invalid
            if (!endTime && trade.start_time) {
              try {
                const startTime = new Date(trade.start_time);
                if (!isNaN(startTime.getTime())) {
                  endTime = new Date(startTime.getTime() + tradeDuration * 1000);
                }
              } catch {
                // Use current time + remaining duration as last resort
                endTime = new Date(Date.now() + tradeDuration * 1000);
              }
            }
            
            // Final fallback
            if (!endTime) {
              endTime = new Date(Date.now() + tradeDuration * 1000);
            }
            
            // Use currentTime state for reactive updates (triggers re-render every second)
            const now = currentTime;
            
            // Calculate remaining time - use end_time for accuracy
            let remainingTime = Math.floor((endTime.getTime() - now.getTime()) / 1000);
            
            // Clamp remaining time to be within valid range [0, tradeDuration]
            remainingTime = Math.max(0, Math.min(tradeDuration, remainingTime));
            
            // Calculate elapsed time based on remaining
            const elapsedSeconds = tradeDuration - remainingTime;
            
            // Calculate progress percentage
            const progress = tradeDuration > 0 ? Math.min(100, (elapsedSeconds / tradeDuration) * 100) : 100;
            const circumference = 2 * Math.PI * 45;
            const strokeDashoffset = circumference - (progress / 100) * circumference;
            
            // Format time display
            const minutes = Math.floor(remainingTime / 60);
            const seconds = remainingTime % 60;
            const timeDisplay = minutes > 0 ? `${minutes}:${seconds.toString().padStart(2, '0')}` : `${seconds}s`;
            
            return (
              <div key={trade.id} className="bg-gradient-to-r from-green-900/40 to-emerald-900/40 border-2 border-green-500 rounded-2xl p-4 sm:p-6 mb-4 shadow-lg shadow-green-500/20">
                {/* Mobile: Stacked layout, Desktop: Side by side */}
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
                  {/* Trade Info */}
                  <div className="flex-1">
                    <div className="flex flex-wrap items-center gap-2 mb-2">
                      <span className="px-2 sm:px-3 py-1 bg-green-500/20 text-green-400 rounded-full text-[10px] sm:text-xs font-bold uppercase tracking-wider">
                        Live Trade
                      </span>
                      <span className={`px-2 sm:px-3 py-1 rounded-full text-[10px] sm:text-xs font-bold ${
                        trade.prediction === 'up' 
                          ? 'bg-green-500/30 text-green-300' 
                          : 'bg-red-500/30 text-red-300'
                      }`}>
                        {trade.prediction === 'up' ? '📈 UP' : '📉 DOWN'}
                      </span>
                    </div>
                    <div className="text-xl sm:text-2xl font-bold text-white mb-2 flex items-center gap-2">
                      {getCommodity(trade.crypto_symbol) && <CryptoIcon symbol={trade.crypto_symbol} size={28} />}
                      {formatPair(trade.crypto_symbol)}
                    </div>
                    <div className="grid grid-cols-2 gap-2 sm:gap-4 text-xs sm:text-sm">
                      <div>
                        <span className="text-gray-400">Entry:</span>
                        <span className="text-white font-semibold ml-1 sm:ml-2">${trade.entry_price?.toLocaleString()}</span>
                      </div>
                      <div>
                        <span className="text-gray-400">Amount:</span>
                        <span className="text-yellow-400 font-semibold ml-1 sm:ml-2">${(trade.amount != null ? fmtFixed(trade.amount, 2) : undefined)}</span>
                      </div>
                      <div>
                        <span className="text-gray-400">Profit:</span>
                        <span className="text-green-400 font-semibold ml-1 sm:ml-2">+${(trade.potential_profit != null ? fmtFixed(trade.potential_profit, 2) : undefined)}</span>
                      </div>
                      <div>
                        <span className="text-gray-400">Using:</span>
                        <span className="text-purple-400 font-semibold ml-1 sm:ml-2">{trade.trade_currency || 'USDT'}</span>
                      </div>
                    </div>
                    
                    {/* Balance Status - Responsive grid */}
                    <div className="mt-3 pt-3 border-t border-green-500/30">
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-[10px] sm:text-xs">
                        <div className="flex items-center">
                          <span className="w-2 h-2 bg-yellow-500 rounded-full mr-1.5 flex-shrink-0"></span>
                          <span className="text-gray-400">Locked:</span>
                          <span className="text-yellow-400 font-semibold ml-1 truncate">
                            {(trade.trade_amount_crypto != null ? fmtFixed(trade.trade_amount_crypto, 4) : undefined) || (trade.amount != null ? fmtFixed(trade.amount, 2) : undefined)} {trade.trade_currency || 'USDT'}
                          </span>
                        </div>
                        <div className="flex items-center">
                          <span className="w-2 h-2 bg-green-500 rounded-full mr-1.5 flex-shrink-0"></span>
                          <span className="text-gray-400">Win:</span>
                          <span className="text-green-400 font-semibold ml-1 truncate">
                            +{fmtFixed(((trade.trade_amount_crypto || trade.amount) * (trade.profit_rate || 0.1)), 4)} {trade.trade_currency || 'USDT'}
                          </span>
                        </div>
                        <div className="flex items-center">
                          <span className="w-2 h-2 bg-red-500 rounded-full mr-1.5 flex-shrink-0"></span>
                          <span className="text-gray-400">Lose:</span>
                          <span className="text-red-400 font-semibold ml-1 truncate">
                            -{(trade.trade_amount_crypto != null ? fmtFixed(trade.trade_amount_crypto, 4) : undefined) || (trade.amount != null ? fmtFixed(trade.amount, 2) : undefined)} {trade.trade_currency || 'USDT'}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Premium Countdown Timer - Centered on mobile */}
                  <div className="flex flex-col items-center mt-4 lg:mt-0 lg:ml-6">
                    <div className="relative w-24 h-24 sm:w-32 sm:h-32">
                      {/* Outer glow effect */}
                      <div className="absolute inset-0 rounded-full bg-gradient-to-r from-purple-500/20 via-cyan-500/20 to-purple-500/20 blur-xl animate-pulse"></div>
                      
                      {/* SVG Timer */}
                      <svg className="w-24 h-24 sm:w-32 sm:h-32 transform -rotate-90 relative z-10" viewBox="0 0 128 128">
                        {/* Outer decorative ring */}
                        <circle
                          cx="64"
                          cy="64"
                          r="60"
                          stroke="rgba(139, 92, 246, 0.1)"
                          strokeWidth="2"
                          fill="none"
                          strokeDasharray="4 4"
                        />
                        
                        {/* Background track */}
                        <circle
                          cx="64"
                          cy="64"
                          r="52"
                          stroke="rgba(30, 30, 50, 0.8)"
                          strokeWidth="8"
                          fill="none"
                        />
                        
                        {/* Gradient progress ring */}
                        <circle
                          cx="64"
                          cy="64"
                          r="52"
                          stroke="url(#timerGradient)"
                          strokeWidth="8"
                          fill="none"
                          strokeLinecap="round"
                          strokeDasharray={2 * Math.PI * 52}
                          strokeDashoffset={2 * Math.PI * 52 - (progress / 100) * 2 * Math.PI * 52}
                          className="transition-all duration-1000 ease-linear"
                          style={{ filter: 'drop-shadow(0 0 6px rgba(139, 92, 246, 0.6))' }}
                        />
                        
                        {/* Inner accent ring */}
                        <circle
                          cx="64"
                          cy="64"
                          r="44"
                          stroke="rgba(139, 92, 246, 0.3)"
                          strokeWidth="1"
                          fill="none"
                        />
                        
                        {/* Tick marks */}
                        {[...Array(12)].map((_, i) => {
                          const angle = (i * 30 - 90) * (Math.PI / 180);
                          const x1 = 64 + 56 * Math.cos(angle);
                          const y1 = 64 + 56 * Math.sin(angle);
                          const x2 = 64 + 52 * Math.cos(angle);
                          const y2 = 64 + 52 * Math.sin(angle);
                          return (
                            <line
                              key={i}
                              x1={x1}
                              y1={y1}
                              x2={x2}
                              y2={y2}
                              stroke="rgba(139, 92, 246, 0.4)"
                              strokeWidth="2"
                              strokeLinecap="round"
                            />
                          );
                        })}
                        
                        <defs>
                          <linearGradient id="timerGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                            <stop offset="0%" stopColor="#8B5CF6" />
                            <stop offset="50%" stopColor="#06B6D4" />
                            <stop offset="100%" stopColor="#8B5CF6" />
                          </linearGradient>
                        </defs>
                      </svg>
                      
                      {/* Center content */}
                      <div className="absolute inset-0 flex flex-col items-center justify-center z-20">
                        <div className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-purple-400 via-cyan-400 to-purple-400 bg-clip-text text-transparent">
                          {timeDisplay}
                        </div>
                        <div className="text-[8px] sm:text-[10px] text-gray-400 uppercase tracking-widest mt-0.5 sm:mt-1">remaining</div>
                        <div className="flex items-center mt-0.5 sm:mt-1">
                          <span className="w-1 h-1 sm:w-1.5 sm:h-1.5 bg-cyan-400 rounded-full animate-pulse mr-1"></span>
                          <span className="text-[8px] sm:text-[9px] text-cyan-400 font-medium">LIVE</span>
                        </div>
                      </div>
                    </div>
                    
                    {/* Duration badge */}
                    <div className="mt-2 sm:mt-3 px-2 sm:px-3 py-1 bg-purple-500/20 border border-purple-500/30 rounded-full">
                      <span className="text-[10px] sm:text-xs text-purple-300 font-medium">{tradeDuration}s Duration</span>
                    </div>
                  </div>
                </div>
                
                {/* Progress Bar */}
                <div className="mt-3 sm:mt-4">
                  <div className="h-1.5 sm:h-2 bg-gray-700 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-gradient-to-r from-green-500 to-emerald-400 rounded-full transition-all duration-1000 ease-linear"
                      style={{ width: `${progress}%` }}
                    ></div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Prediction Selection */}
      <div className="mb-4 sm:mb-6">
        <label className="text-sm font-semibold text-gray-300 mb-3 block">Your Prediction</label>
        <div className="grid grid-cols-2 gap-3 sm:gap-4">
          <button
            onClick={() => setPrediction('up')}
            data-testid="binary-predict-up-btn"
            disabled={isSubmitting}
            className={`py-4 sm:py-6 rounded-xl font-bold transition-all flex flex-col items-center justify-center space-y-2 ${
              prediction === 'up'
                ? 'bg-gradient-to-r from-green-500 to-green-600 text-white shadow-lg shadow-green-500/50 scale-105'
                : 'bg-gray-800/50 text-gray-400 hover:bg-gray-800'
            } ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            <TrendingUp className="h-8 w-8" />
            <span className="text-lg">UP</span>
          </button>
          <button
            onClick={() => setPrediction('down')}
            data-testid="binary-predict-down-btn"
            disabled={isSubmitting}
            className={`py-4 sm:py-6 rounded-xl font-bold transition-all flex flex-col items-center justify-center space-y-2 ${
              prediction === 'down'
                ? 'bg-gradient-to-r from-red-500 to-red-600 text-white shadow-lg shadow-red-500/50 scale-105'
                : 'bg-gray-800/50 text-gray-400 hover:bg-gray-800'
            } ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            <TrendingDown className="h-8 w-8" />
            <span className="text-lg">DOWN</span>
          </button>
        </div>
      </div>

      {/* Duration Selection */}
      <div className="mb-4 sm:mb-6">
        <label className="text-sm font-semibold text-gray-300 mb-3 block">Trade Duration</label>
        <div className="grid grid-cols-4 sm:grid-cols-7 gap-2">
          {durations.map((d) => (
            <button
              key={d.value}
              onClick={() => setDuration(d.value.toString())}
              data-testid={`binary-duration-${d.value}`}
              disabled={isSubmitting}
              className={`py-2 sm:py-3 rounded-lg font-semibold transition-all text-xs sm:text-sm ${
                duration === d.value.toString()
                  ? 'bg-purple-600 text-white shadow-lg shadow-purple-500/50'
                  : 'bg-gray-800 text-gray-400 hover:bg-gray-700 hover:text-white'
              } ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              {d.label}
            </button>
          ))}
        </div>
        {selectedDuration && (
          <div className="mt-2 text-xs text-gray-400">
            Amount range: ${selectedDuration.minAmount.toLocaleString()} - ${selectedDuration.maxAmount.toLocaleString()} | Profit: {(selectedDuration.profitRate * 100).toFixed(0)}%
          </div>
        )}
      </div>

      {/* Amount Input */}
      <div className="mb-4 sm:mb-6">
        <label className="text-sm font-semibold text-gray-300 mb-3 block">Investment Amount (USDT)</label>
        <input
          type="number"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          data-testid="binary-amount-input"
          placeholder="0.00"
          disabled={isSubmitting}
          className="w-full px-4 py-3 bg-gray-800 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-lg font-semibold disabled:opacity-50"
          step="1"
        />
      </div>

      {/* Potential Return Display */}
      <div className="bg-gradient-to-r from-purple-900/50 to-blue-900/50 rounded-lg p-3 sm:p-4 mb-4 sm:mb-6 border border-purple-500/30">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs sm:text-sm text-gray-400">Investment</span>
          <span className="text-base sm:text-lg font-bold text-white">${amount ? fmtNumber(amount, 2) : '0.00'}</span>
        </div>
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs sm:text-sm text-gray-400">Potential Return ({returns.profitRate})</span>
          <span className="text-base sm:text-lg font-bold text-green-400">${returns.return}</span>
        </div>
        <div className="border-t border-purple-500/30 pt-2 mt-2">
          <div className="flex items-center justify-between">
            <span className="text-xs sm:text-sm font-semibold text-gray-300">Potential Profit</span>
            <div className="flex items-center space-x-1 sm:space-x-2">
              <DollarSign className="h-4 w-4 sm:h-5 sm:w-5 text-yellow-400" />
              <span className="text-lg sm:text-xl font-bold text-yellow-400">+${returns.profit}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Place Trade Button */}
      <button
        onClick={handleTrade}
        data-testid="binary-place-trade-btn"
        disabled={isSubmitting || !amount}
        className={`w-full py-3 sm:py-4 md:py-5 rounded-xl font-bold text-base sm:text-lg transition-all shadow-lg ${
          prediction === 'up'
            ? 'bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white shadow-green-500/50'
            : 'bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white shadow-red-500/50'
        } disabled:opacity-50 disabled:cursor-not-allowed`}
      >
        {isSubmitting ? 'Placing Trade...' : `Place ${prediction.toUpperCase()} Trade`}
      </button>

      {/* Risk Warning */}
      <div className="mt-3 sm:mt-4 p-2 sm:p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
        <p className="text-[10px] sm:text-xs text-yellow-400 text-center">
          ⚠️ Binary trading involves high risk. Only invest what you can afford to lose.
        </p>
      </div>
    </div>
    </div>
  );
};

export default BinaryTradingView;
